import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Menu, X, Phone, MessageCircle, User as UserIcon } from "lucide-react";
import { BUSINESS, NAV_LINKS, WA_DEFAULT } from "@/data/business";
import { useBooking } from "@/components/booking";
import { useAuth } from "@/components/auth";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { openBooking } = useBooking();
  const { user, login } = useAuth();
  const location = useLocation();
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const darkPage = location.pathname === "/bridal-makeup";
  const light = darkPage && !scrolled; // render header in ivory tones over dark hero

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled
            ? "border-b border-charcoal/8 bg-ivory/90 shadow-[0_8px_30px_rgba(27,25,22,0.06)] backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-all duration-300 sm:px-8 ${
            scrolled ? "py-3" : "py-5"
          }`}
        >
          <Link to="/" data-testid="header-logo" className="group leading-none">
            <span className={`font-serif text-[26px] font-medium italic tracking-tight transition-colors ${light ? "text-ivory" : "text-charcoal"}`}>
              Glam Girl
            </span>
            <span className={`mt-0.5 block text-[9px] font-semibold uppercase tracking-[0.34em] ${light ? "text-champagne" : "text-rose"}`}>
              Makeup Studio
            </span>
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors hover:text-rose ${
                    isActive
                      ? "text-rose"
                      : light
                        ? "text-ivory/75 hover:text-champagne"
                        : "text-charcoal/75"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <Link
                to="/account"
                data-testid="header-account-link"
                className={`hidden items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-4 transition-colors sm:flex ${
                  light ? "border-ivory/25 text-ivory hover:border-champagne" : "border-charcoal/15 text-charcoal hover:border-rose"
                }`}
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-rose text-[11px] font-bold text-ivory">
                    {user.name?.[0]?.toUpperCase() || "G"}
                  </span>
                )}
                <span className="max-w-[90px] truncate text-[12px] font-semibold">{user.name?.split(" ")[0]}</span>
              </Link>
            ) : (
              <button
                onClick={login}
                data-testid="header-signin-button"
                className={`hidden items-center gap-2 rounded-full border px-5 py-2.5 text-[13px] font-semibold tracking-wide transition-colors sm:inline-flex ${
                  light
                    ? "border-ivory/25 text-ivory hover:border-champagne hover:text-champagne"
                    : "border-charcoal/20 text-charcoal hover:border-rose hover:text-rose"
                }`}
              >
                <UserIcon className="h-3.5 w-3.5" /> Sign In
              </button>
            )}
            <button
              onClick={() => openBooking()}
              data-testid="header-book-button"
              className={`hidden rounded-full px-6 py-2.5 text-[13px] font-semibold tracking-wide transition-colors sm:block ${
                light ? "bg-ivory text-ink hover:bg-champagne" : "bg-charcoal text-ivory hover:bg-rose-deep"
              }`}
            >
              Book Appointment
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                light ? "border-ivory/25 text-ivory" : "border-charcoal/15 text-charcoal"
              } lg:hidden`}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink text-ivory"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-serif text-2xl italic">Glam Girl</span>
              <button
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-ivory/20"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav aria-label="Mobile" className="flex flex-1 flex-col justify-center gap-1 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={reduce ? false : { opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.45 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="block border-b border-ivory/10 py-4 font-serif text-4xl font-medium tracking-tight transition-colors hover:text-champagne"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.45 }}
              className="space-y-3 px-8 pb-10"
            >
              <button
                onClick={() => { setMenuOpen(false); openBooking(); }}
                data-testid="mobile-menu-book-button"
                className="w-full rounded-full bg-ivory py-4 text-sm font-semibold tracking-wide text-ink"
              >
                Book Appointment
              </button>
              {user ? (
                <Link
                  to="/account"
                  data-testid="mobile-menu-account-link"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-ivory/20 py-3.5 text-sm font-medium"
                >
                  <UserIcon className="h-4 w-4" /> My Account
                </Link>
              ) : (
                <button
                  onClick={login}
                  data-testid="mobile-menu-signin-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-ivory/20 py-3.5 text-sm font-medium"
                >
                  <UserIcon className="h-4 w-4" /> Sign In with Google
                </button>
              )}
              <div className="flex gap-3">
                <a href={BUSINESS.phoneTel} data-testid="mobile-menu-call" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-ivory/20 py-3.5 text-sm font-medium">
                  <Phone className="h-4 w-4" /> Call
                </a>
                <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-ivory/20 py-3.5 text-sm font-medium">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
              <p className="pt-2 text-center text-xs text-ivory/40">{BUSINESS.hoursLine}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
